/** Map a click in a text node to a caret offset in the element's plain text. */
export function caretOffsetFromPoint(
  root: HTMLElement,
  clientX: number,
  clientY: number,
): number | null {
  const doc = root.ownerDocument as Document & {
    caretPositionFromPoint?: (
      x: number,
      y: number,
    ) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  let node: Node | null = null;
  let offset = 0;
  if (typeof doc.caretPositionFromPoint === "function") {
    const pos = doc.caretPositionFromPoint(clientX, clientY);
    if (pos) {
      node = pos.offsetNode;
      offset = pos.offset;
    }
  }
  if (!node && typeof doc.caretRangeFromPoint === "function") {
    const range = doc.caretRangeFromPoint(clientX, clientY);
    if (range) {
      node = range.startContainer;
      offset = range.startOffset;
    }
  }
  if (!node || (node !== root && !root.contains(node))) return null;
  const range = doc.createRange();
  try {
    range.setStart(root, 0);
    range.setEnd(node, offset);
  } catch {
    return null;
  }
  return textLengthIn(range.cloneContents());
}

function textLengthIn(root: Node): number {
  let n = 0;
  const walk = (el: Node) => {
    if (el.nodeType === Node.TEXT_NODE) {
      n += el.textContent?.length ?? 0;
      return;
    }
    if ((el as Element).nodeName === "BR") {
      n += 1;
      return;
    }
    el.childNodes.forEach(walk);
  };
  walk(root);
  return n;
}

/** Focus an input and put a collapsed caret at `offset` (clamped). */
export function placeCaretInInput(
  el: HTMLInputElement | HTMLTextAreaElement,
  offset: number | null,
  fallback: "start" | "end" = "end",
): void {
  const max = el.value.length;
  const pos =
    offset == null
      ? fallback === "start"
        ? 0
        : max
      : Math.min(Math.max(0, offset), max);
  el.focus();
  el.setSelectionRange(pos, pos);
  requestAnimationFrame(() => {
    if (document.activeElement !== el) return;
    el.setSelectionRange(pos, pos);
  });
}
